import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import TestimonialCard from './TestimonialCard';
import { useInView, usePrefersReducedMotion } from './features/animations/AnimationUtils';

// Testimonial data structure - easy to manage and rank
const testimonials = [
  {
    id: 1,
    ranking: 1, // Top testimonial
    quote: "WaveScoop = better ideas, safer posts, happier team. It's baked into our workflow.",
    author: "Conor McGrath",
    role: "Founder",
    company: "One Ocean Network",
    avatar: "CM",
    avatarColor: "#FF6B6B"
  }
];

export default function Testimonials() {
  // Animation hooks
  const [headerRef, isHeaderInView] = useInView({ threshold: 0.1 });
  const prefersReducedMotion = usePrefersReducedMotion();
  
  // Sort testimonials by ranking
  const sortedTestimonials = [...testimonials].sort((a, b) => a.ranking - b.ranking);
  
  // Get top 2 testimonials for featured section
  const featuredTestimonials = sortedTestimonials.slice(0, 2);
  
  // Get next 6 testimonials for grid section
  const additionalTestimonials = sortedTestimonials.slice(2, 8);

  return (
    <Box 
      component="section" 
      sx={{ 
        py: { xs: 8, md: 12 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box ref={headerRef} sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 800,
              color: 'white',
              mb: 2,
              opacity: isHeaderInView || prefersReducedMotion ? 1 : 0,
              transform: isHeaderInView || prefersReducedMotion ? 'translateY(0)' : 'translateY(20px)',
              transition: prefersReducedMotion ? 'none' : 'all 600ms ease-out',
            }}
          >
            Why Marketers are Switching to WaveScoop
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '1rem', md: '1.1rem' },
              color: 'rgba(255, 255, 255, 0.8)',
              maxWidth: 600,
              mx: 'auto',
              opacity: isHeaderInView || prefersReducedMotion ? 1 : 0,
              transform: isHeaderInView || prefersReducedMotion ? 'translateY(0)' : 'translateY(20px)',
              transition: prefersReducedMotion ? 'none' : 'all 600ms ease-out 200ms',
            }}
          >
            Real feedback from marketers, founders, and teams building their brands online.
          </Typography>
        </Box>

        {/* Featured Testimonials - Top 2 */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {featuredTestimonials.map((testimonial) => (
            <Grid item xs={12} md={6} key={testimonial.id}>
              <TestimonialCard
                quote={testimonial.quote}
                author={testimonial.author}
                role={testimonial.role}
                company={testimonial.company}
                avatar={testimonial.avatar}
                avatarColor={testimonial.avatarColor}
                size="full"
              />
            </Grid>
          ))}
          
          {/* CTA Card if 2 or fewer featured testimonials */}
          {sortedTestimonials.length <= 2 && (
            <Grid item xs={12} md={sortedTestimonials.length === 1 ? 6 : 12}>
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  backgroundColor: 'rgba(10, 124, 160, 0.15)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  border: '2px dashed rgba(10, 124, 160, 0.4)',
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  minHeight: { xs: 180, md: 200 },
                }}
              >
                <Typography
                  sx={{
                    color: 'white',
                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Want to be featured here?
                </Typography>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: { xs: '0.9rem', md: '1rem' },
                    mb: 3,
                  }}
                >
                  Share your WaveScoop success story
                </Typography>
                <Box
                  component="a"
                  href="mailto:hello@wavescoop.com?subject=WaveScoop%20Success%20Story"
                  sx={{
                    display: 'inline-block',
                    backgroundColor: '#0a7ca0',
                    color: 'white',
                    px: 3,
                    py: 1.5,
                    borderRadius: '28px',
                    textDecoration: 'none',
                    fontSize: { xs: '0.9rem', md: '1rem' },
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: '#0a8cb3',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(10, 124, 160, 0.3)',
                    },
                  }}
                >
                  Contact us
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Additional Testimonials - Grid */}
        <Grid container spacing={2}>
          {additionalTestimonials.map((testimonial) => (
            <Grid item xs={12} sm={6} md={3} key={testimonial.id}>
              <TestimonialCard
                quote={testimonial.quote}
                author={testimonial.author}
                role={testimonial.role}
                company={testimonial.company}
                avatar={testimonial.avatar}
                avatarColor={testimonial.avatarColor}
                size="compact"
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}